import React from "react";
import moment from "moment";

import StatusBadge from "designV2/components/StatusBadge";
import {
  DetailField,
  CopyableField,
  ImageStrip,
  assetUrl,
  humanize,
} from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : null);
const formatDateTime = (value) =>
  value ? moment(value).format("MMM D, YYYY [at] hh:mm A") : null;

const STATUS_TONES = { APPROVED: "success", REJECTED: "danger", PENDING: "warning" };

// The driver submits a new set of documents each time, so the request
// being reviewed is the most recent one — the previous screen read
// verificationRequests[0], which is the oldest.
export const latestRequest = (driver) => {
  const requests = (driver && driver.verificationRequests) || [];
  if (!requests.length) return null;
  return requests
    .slice()
    .sort((a, b) => new Date(b.submitDate || 0) - new Date(a.submitDate || 0))[0];
};

const expiryNote = (value) => {
  if (!value) return null;
  const expired = moment(value).isBefore(moment());
  return (
    <span style={{ color: expired ? "var(--color-accent-danger-fg)" : undefined }}>
      {formatDate(value)}
      {expired ? " · expired" : ""}
    </span>
  );
};

/**
 * VerificationDetails - content-only, rendered inside a DialogShell.
 *
 * Shows the documents the driver submitted alongside the details they
 * claim, which is the whole point of the screen — the previous version
 * put each document behind its own react-image-lightbox and showed the
 * expiry dates as plain text with no indication that one had passed.
 */
const VerificationDetails = ({ item }) => {
  if (!item) return null;

  const request = latestRequest(item);
  const details = (request && request.verificationDetails) || {};
  const licence = details.drivingLicence || item.drivingLicence || {};

  const licencePhotos = Array.isArray(licence.photoUrl)
    ? licence.photoUrl
    : [licence.photoUrl];

  return (
    <div className="superPersonDetails">
      <div className="superPersonDetailsTop">
        <StatusBadge
          status={STATUS_TONES[request && request.status] || "neutral"}
          label={humanize(request && request.status) || "No request"}
        />
        <StatusBadge
          status={item.state === "SUSPENDED" ? "danger" : "success"}
          label={humanize(item.state) || "Unknown"}
          dot={false}
        />
        <span className="superPersonDetailsHeadline">{item.fullName || "Unnamed"}</span>
      </div>

      <div className="superPersonGrid">
        <CopyableField label="Driver ID" value={item._id} />
        <DetailField label="Submitted" value={formatDateTime(request && request.submitDate)} />
        <DetailField label="Phone" value={item.phoneNumber} />
        <DetailField label="Email" value={item.email} />
        <DetailField
          label="Gender"
          value={humanize(details.gender || item.gender)}
        />
        <DetailField
          label="Date of birth"
          value={formatDate(details.birthDate || item.birthDate)}
        />
        <DetailField
          label="Address"
          value={
            details.address && details.address.full
              ? `${details.address.full}${
                  details.address.zipCode ? ` · ${details.address.zipCode}` : ""
                }`
              : null
          }
          wide
        />
        <CopyableField label="Licence number" value={licence.licenceId} />
        <DetailField label="Licence expires" value={expiryNote(licence.expireDate)} />
        <DetailField
          label="Work permit expires"
          value={expiryNote(details.canadianVerificationExpireDate)}
        />
        <DetailField
          label="Vehicle"
          value={
            item.defaultCar
              ? `${item.defaultCar.plate || ""}${
                  item.defaultCar.carType ? ` · ${item.defaultCar.carType.type}` : ""
                }`
              : null
          }
        />
        <DetailField label="Joined" value={formatDate(item.createdAt)} />
      </div>

      <ImageStrip label="Profile photo" urls={[assetUrl(details.profileImageUrl)]} />
      <ImageStrip label="Driving licence" urls={licencePhotos.map(assetUrl)} />
      <ImageStrip
        label="Driving record"
        urls={[assetUrl(details.drivingRecordPhotoUrl)]}
      />
      <ImageStrip
        label="Work eligibility"
        urls={[assetUrl(details.canadianVerificationPhotoUrl)]}
      />
      <ImageStrip
        label="Background check"
        urls={[assetUrl(details.backgroundCheckDocumentPhotoUrl)]}
      />
    </div>
  );
};

export default VerificationDetails;
